import React, { useEffect, useMemo } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import SEO from '../../SEO';
import { useAuth, isFounderUser, getDashboardPath, isAdminUser } from '../../../context/AuthContext';
import DashboardLayout from '../shared/DashboardLayout';
import { FounderProvider, useFounder } from './FounderContext';
import { getFounderNav, FOUNDER_TITLES } from './nav';

function FounderLayoutInner({ user, onLogout }) {
  const location = useLocation();
  const { startup } = useFounder();
  const s = user?.startups?.[0] || (startup
    ? {
        stage: startup.lifecycleStage || startup.stage || startup.status,
        name: startup.name,
        skipOnboarding: startup.entryPath === 'active_existing',
        entryPath: startup.entryPath,
        dueDiligenceIsOpen: false,
      }
    : null);

  const navItems = useMemo(
    () =>
      getFounderNav(s, {
        needsApplyForm: user?.needsApplyForm || s?.needsApplyForm,
        dueDiligenceIsOpen: s?.dueDiligenceIsOpen,
      }),
    [s, user]
  );

  const getTitle = () => FOUNDER_TITLES[location.pathname] || 'Founder Portal';

  return (
    <DashboardLayout
      brand="Founder Portal"
      subtitle={s?.name}
      navItems={navItems}
      user={{ ...user, name: user.fullName || user.name }}
      onLogout={onLogout}
      getTitle={getTitle}
    >
      <Outlet />
    </DashboardLayout>
  );
}

function FounderShell() {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !user) navigate('/login');
    if (user && !isFounderUser(user)) {
      if (isAdminUser(user)) navigate('/admin');
      else navigate(getDashboardPath(user));
    }
  }, [user, authLoading, navigate]);

  if (authLoading || !user) return null;

  const startupId = user.startups?.[0]?.id || user.startupId;

  return (
    <FounderProvider startupId={startupId}>
      <SEO title="Founder Dashboard" description="InnovHer founder portal for application, onboarding and batch work." />
      <FounderLayoutInner
        user={user}
        onLogout={() => {
          logout();
          navigate('/login');
        }}
      />
    </FounderProvider>
  );
}

export default function FounderLayout() {
  return <FounderShell />;
}
